import { useState, useEffect, useRef } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { bouncy } from "ldrs";
import Select from "react-select";
import {
    AddAttendance,
    EditAttendance,
    GetCircleByZoneId,
    GetEmployeesByWard,
    GetActiveEmployees,
    GetAttendanceZones
} from "../AttendanceApis";
import { getCurrentDate } from "../../../Common/helper/helpar";
bouncy.register();

const formatDateTimeLocal = (date, time) => {
    if (!date || !time) return "";
    const normalizedTime = String(time).split(".")[0];
    const [hour, minute] = normalizedTime.split(":");
    if (!hour || !minute) return "";
    return `${date}T${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
};

const toArray = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
};

const resolveWardId = (editData, circles = []) => {
    const wardLabels = [
        editData?.circleName,
        editData?.wardName,
        editData?.wardNo,
    ].filter(Boolean);

    for (const label of wardLabels) {
        const matchedWard = circles.find(
            (circle) =>
                String(circle.circleName ?? "").toLowerCase() ===
                String(label).toLowerCase()
        );
        if (matchedWard?.circleId) {
            return String(matchedWard.circleId);
        }
    }

    const storedWardId =
        editData?.wardId ?? editData?.circleId ?? editData?.WardId ?? null;

    return storedWardId ? String(storedWardId) : null;
};

const resolveEmployeeId = (editData, employees = []) => {
    const normalizedEditName = String(editData?.employeeName ?? "")
        .trim()
        .toLowerCase();
    const normalizedEditCode = String(
        editData?.employeeCode ?? editData?.empCode ?? ""
    )
        .trim()
        .toLowerCase();

    if (normalizedEditName || normalizedEditCode) {
        const matchedEmployee = employees.find((employee) => {
            const employeeName = String(
                employee.employeeName ?? employee.empName ?? employee.name ?? ""
            )
                .trim()
                .toLowerCase();
            const employeeCode = String(
                employee.employeeCode ?? employee.empCode ?? ""
            )
                .trim()
                .toLowerCase();

            return (
                (normalizedEditName && employeeName === normalizedEditName) ||
                (normalizedEditCode && employeeCode === normalizedEditCode)
            );
        });

        if (matchedEmployee) {
            return String(
                matchedEmployee.employeeId ??
                matchedEmployee.EmployeeId ??
                matchedEmployee.empId ??
                ""
            );
        }
    }

    const storedEmployeeId =
        editData?.employeeId ?? editData?.empId ?? editData?.EmployeeId ?? null;

    return storedEmployeeId ? String(storedEmployeeId) : null;
};

export const AddAttendanceModal = ({
    showModal,
    closeModal,
    editData,
    isEditAttendance,
    setIsEditAttendance,
    fetchAttendance,
    shiftOptions,
    zoneOptions = [],
    wardOptions = [],
    employeeOptions = [],
}) => {
    const [loading, setLoading] = useState(false);
    const [filteredWards, setFilteredWards] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [zones, setZones] = useState([]);

    const formikRef = useRef(null);

    const getActiveZones = () => {
        const sourceZones = zones.length ? zones : zoneOptions;
        return sourceZones.filter(
            (zone) =>
                zone.isActive !== false
        );
    };

    const getDefaultZoneId = () => {
        if (isEditAttendance) {
            return editData?.zoneId ?? editData?.ZoneId ?? "";
        }
        const activeZones = getActiveZones();
        return activeZones[0]?.zoneId ?? activeZones[0]?.ZoneId ?? "";
    };

    const fetchCirclesByZone = async (zoneId) => {
        if (!zoneId) {
            setFilteredWards([]);
            return [];
        }
        const raw = await GetCircleByZoneId(zoneId);
        const circles = toArray(raw);
        setFilteredWards(circles);
        return circles;
    };

    useEffect(() => {
        if (!showModal) return;
        const filterDelhiZones = (list) =>
            toArray(list).filter(
                (zone) =>
                    zone.isActive !== false
            );

        // Prefer parent-provided zones when available
        if (Array.isArray(zoneOptions) && zoneOptions.length) {
            setZones(filterDelhiZones(zoneOptions));
            return;
        }
        const loadZones = async () => {
            const raw = await GetAttendanceZones();
            setZones(filterDelhiZones(raw));
        };
        loadZones();
    }, [showModal, zoneOptions]);

    useEffect(() => {
        if (!showModal) return;
        const loadForEdit = async () => {
            if (!isEditAttendance || !editData) {
                setFilteredWards([]);
                setFilteredEmployees([]);
                return;
            }

            // Resolve zoneId: prefer editData.zoneId/ZoneId, otherwise match by name
            let zoneId = editData?.zoneId ?? editData?.ZoneId ?? null;
            if (!zoneId && (editData?.zoneName || editData?.ZoneName)) {
                const zoneName = editData?.zoneName ?? editData?.ZoneName;
                const sourceZones = Array.isArray(zoneOptions) && zoneOptions.length ? zoneOptions : zones;
                const matchedZone = sourceZones.find(
                    (z) =>
                        String(z.zoneName ?? z.ZoneName ?? "").toLowerCase() === String(zoneName ?? "").toLowerCase()
                );
                zoneId = matchedZone?.zoneId ?? matchedZone?.ZoneId ?? null;
            }

            if (!zoneId) {
                setFilteredWards([]);
                setFilteredEmployees([]);
                return;
            }

            const circles = await fetchCirclesByZone(zoneId);
            const wardId = resolveWardId(editData, circles);

            let shiftId = editData?.shiftId ?? editData?.ShiftId ?? null;
            if (!shiftId && (editData?.masterShift || editData?.actualShift)) {
                const shiftName = editData?.masterShift ?? editData?.actualShift;
                const normalizedShiftName = String(shiftName ?? "").toLowerCase();
                const matchedShift = shiftOptions.find((s) => {
                    const names = [s.shiftName, s.ShiftName, s.shiftCode, s.shiftLabel]
                        .filter(Boolean)
                        .map((v) => String(v).toLowerCase());
                    const combinedTime = `${s.startTime ?? ""}-${s.endTime ?? ""}`.toLowerCase();
                    return (
                        names.includes(normalizedShiftName) ||
                        combinedTime.includes(normalizedShiftName) ||
                        normalizedShiftName.includes(names.find(Boolean) ?? "")
                    );
                });
                shiftId = matchedShift?.shiftId ?? matchedShift?.ShiftId ?? null;
            }

            const emps = wardId ? toArray(await GetActiveEmployees(zoneId, wardId)) : [];
            setFilteredEmployees(emps);

            const empId = resolveEmployeeId(editData, emps);

            setTimeout(() => {
                if (!formikRef.current) return;
                formikRef.current.setFieldValue("ZoneId", String(zoneId));
                formikRef.current.setFieldValue("WardId", wardId ?? "");
                formikRef.current.setFieldValue("EmployeeId", empId ?? "");
                if (shiftId) formikRef.current.setFieldValue("ShiftId", shiftId);
            }, 50);
        };
        loadForEdit();
    }, [showModal, isEditAttendance, editData, zoneOptions, zones, shiftOptions]);

    useEffect(() => {
        if (!showModal || isEditAttendance) return;

        const activeZones = getActiveZones();
        if (activeZones.length === 0) return;

        const firstZoneId = activeZones[0].zoneId ?? activeZones[0].ZoneId;
        fetchCirclesByZone(firstZoneId);
    }, [showModal, isEditAttendance, zones, zoneOptions]);

    const handleZoneChange = async (e, setFieldValue) => {
        const zoneId = e.target.value;
        setFieldValue("ZoneId", zoneId);
        setFieldValue("WardId", "");
        setFieldValue("EmployeeId", "");
        setFilteredEmployees([]);
        if (!zoneId) {
            setFilteredWards([]);
            return;
        }
        const raw = await GetCircleByZoneId(zoneId);
        setFilteredWards(toArray(raw));
    };

    const handleWardChange = async (e, setFieldValue, currentZoneId) => {
        const wardId = e.target.value;
        setFieldValue("WardId", wardId);
        setFieldValue("EmployeeId", "");
        if (!wardId) {
            setFilteredEmployees([]);
            return;
        }
        // Use active employees endpoint with zoneId and circleId (circleId == wardId)
        const raw = await GetActiveEmployees(currentZoneId, wardId);
        setFilteredEmployees(toArray(raw));
    };

    const deriveShiftId = () => {
        const explicitShiftId = editData?.shiftId ?? editData?.ShiftId ?? null;
        if (explicitShiftId) return explicitShiftId;

        const shiftName = editData?.masterShift ?? editData?.actualShift ?? editData?.shiftName ?? editData?.ShiftName;
        if (!shiftName) return "";

        const matchedShift = shiftOptions.find((s) => {
            const names = [s.shiftName, s.ShiftName, s.shiftCode, s.shiftLabel]
                .filter(Boolean)
                .map((v) => String(v).toLowerCase());
            return names.includes(String(shiftName).toLowerCase());
        });
        return matchedShift?.shiftId ?? matchedShift?.ShiftId ?? "";
    };

    const initialValues = {
        ZoneId: getDefaultZoneId(),
        WardId: isEditAttendance ? editData?.wardId ?? editData?.circleId ?? editData?.WardId ?? "" : "",
        EmployeeId: isEditAttendance ? editData?.employeeId ?? editData?.empId ?? editData?.EmployeeId ?? "" : "",
        PunchInTime: isEditAttendance ? formatDateTimeLocal(editData?.date ?? editData?.Date, editData?.punchInTime ?? editData?.PunchInTime ?? editData?.punchIn ?? "") : "",
        PunchOutTime: isEditAttendance ? formatDateTimeLocal(editData?.date ?? editData?.Date, editData?.punchOutTime ?? editData?.PunchOutTime ?? editData?.punchOut ?? "") : "",
        ShiftId: isEditAttendance ? deriveShiftId() : "",
    };

    const validationSchema = Yup.object({
        ZoneId: Yup.string().required("Zone is required"),
        WardId: Yup.string().required("Ward is required"),
        EmployeeId: Yup.string().required("Employee is required"),
        PunchInTime: Yup.string().required("Punch In Time is required"),
        PunchOutTime: Yup.string()
            .required("Punch Out Time is required")
            .test(
                "is-after-punchIn",
                "Punch Out must be after Punch In",
                function (value) {
                    const { PunchInTime } = this.parent;
                    if (!PunchInTime || !value) return true;
                    return value > PunchInTime;
                }
            ),
        ShiftId: Yup.string().required("Shift is required"),
    });

    const handleSubmit = async (values, { resetForm }) => {
        console.log("AddAttendanceModal: handleSubmit called", values);
        setLoading(true);
        try {
            if (isEditAttendance) {
                const payload = { ...values, AttendanceId: editData?.attendanceId };
                const response = await EditAttendance(payload);
                if (response?.status === 200) {
                    toast.success(response?.data?.message ?? "Attendance updated successfully.");
                    closeModal();
                    fetchAttendance();
                    setIsEditAttendance(false);
                    resetForm();
                } else {
                    toast.error(response?.response?.data ?? "Update failed.");
                }
            } else {
                const response = await AddAttendance(values);
                if (response?.status === 200) {
                    toast.success(response?.data?.message ?? "Attendance added successfully.");
                    closeModal();
                    fetchAttendance();
                    resetForm();
                } else {
                    toast.error(response?.response?.data ?? "Add failed.");
                }
            }
        } catch (error) {
            toast.error("Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            size="md"
            show={showModal}
            onHide={closeModal}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header style={{ borderBottom: "1px solid #e5e7eb", padding: "16px 20px" }}>
                <Modal.Title style={{ fontSize: "16px", fontWeight: 600 }}>
                    {isEditAttendance ? "Edit" : "Add"} Attendance
                </Modal.Title>
                <i
                    className="fa-solid fa-xmark close-container"
                    style={{ cursor: "pointer", fontSize: "18px", color: "#6b7280" }}
                    onClick={closeModal}
                ></i>
            </Modal.Header>

            <Modal.Body className="small" style={{ padding: "20px" }}>
                <Formik
                    key={
                        isEditAttendance
                            ? `edit-${editData?.attendanceId ?? editData?.AttendanceId ?? "record"}`
                            : "add"
                    }
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ setFieldValue, values }) => (
                        <Form onSubmitCapture={() => console.log('AddAttendanceModal: onSubmitCapture', formikRef.current?.values, formikRef.current?.errors)}>
                            <Row className="g-3">

                                <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="ZoneId" className="form-label">
                                            Zone <span style={{ color: "#EC1117" }}>*</span>
                                        </label>

                                        <Select
                                            name="ZoneId"
                                            options={zones.map(z => ({
                                                value: z.zoneId ?? z.ZoneId,
                                                label: z.zoneName ?? z.ZoneName
                                            }))}
                                            value={
                                                zones
                                                    .map(z => ({
                                                        value: z.zoneId ?? z.ZoneId,
                                                        label: z.zoneName ?? z.ZoneName
                                                    }))
                                                    .find(option => option.value === values.ZoneId)
                                            }
                                            onChange={(selectedOption) => {
                                                setFieldValue("ZoneId", selectedOption?.value || "");
                                                handleZoneChange(
                                                    {
                                                        target: {
                                                            value: selectedOption?.value || ""
                                                        }
                                                    },
                                                    setFieldValue
                                                );
                                            }}
                                            placeholder="Select Zone"
                                            isSearchable
                                        />

                                        <ErrorMessage
                                            name="ZoneId"
                                            component="div"
                                            style={{ color: "red", fontSize: "12px" }}
                                        />
                                    </div>
                                </Col>

                                {/* <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="ZoneId" className="form-label">
                                            Zone <span style={{ color: "#EC1117" }}>*</span>
                                        </label>
                                        <Field
                                            as="select"
                                            name="ZoneId"
                                            id="ZoneId"
                                            className="form-select"
                                            onChange={(e) => handleZoneChange(e, setFieldValue)}
                                        >
                                            {zones.map((z) => (
                                                <option
                                                    key={z.zoneId ?? z.ZoneId}
                                                    value={z.zoneId ?? z.ZoneId}
                                                >
                                                    {z.zoneName ?? z.ZoneName}
                                                </option>
                                            ))}
                                        </Field>
                                        <ErrorMessage name="ZoneId" component="div" style={{ color: "red", fontSize: "12px" }} />
                                    </div>
                                </Col> */}

                                {/* <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="WardId" className="form-label">
                                            Ward <span style={{ color: "#EC1117" }}>*</span>
                                        </label>
                                        <Field
                                            as="select"
                                            name="WardId"
                                            id="WardId"
                                            className="form-select"
                                            onChange={(e) => handleWardChange(e, setFieldValue, values.ZoneId)}
                                            disabled={!values.ZoneId}
                                        >
                                            <option value="">Select Ward</option>
                                            {filteredWards.map((w) => (
                                                <option key={w.circleId} value={w.circleId}>
                                                    {w.circleName}
                                                </option>
                                            ))}
                                        </Field>
                                        <ErrorMessage name="WardId" component="div" style={{ color: "red", fontSize: "12px" }} />
                                    </div>
                                </Col> */}

                                <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="WardId" className="form-label">
                                            Ward <span style={{ color: "#EC1117" }}>*</span>
                                        </label>

                                        <Select
                                            name="WardId"
                                            options={filteredWards.map((w) => ({
                                                value: w.circleId,
                                                label: w.circleName,
                                            }))}
                                            value={filteredWards
                                                .map((w) => ({
                                                    value: w.circleId,
                                                    label: w.circleName,
                                                }))
                                                .find((option) => option.value === values.WardId)}
                                            onChange={(selectedOption) => {
                                                setFieldValue("WardId", selectedOption?.value || "");

                                                handleWardChange(
                                                    {
                                                        target: {
                                                            value: selectedOption?.value || "",
                                                        },
                                                    },
                                                    setFieldValue,
                                                    values.ZoneId
                                                );
                                            }}
                                            placeholder="Select Ward"
                                            isSearchable
                                            isDisabled={!values.ZoneId}
                                        />

                                        <ErrorMessage
                                            name="WardId"
                                            component="div"
                                            style={{ color: "red", fontSize: "12px" }}
                                        />
                                    </div>
                                </Col>

                                {/* <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="EmployeeId" className="form-label">
                                            Employee <span style={{ color: "#EC1117" }}>*</span>
                                        </label>
                                        <Field
                                            as="select"
                                            name="EmployeeId"
                                            id="EmployeeId"
                                            className="form-select"
                                            disabled={!values.WardId}
                                        >
                                            <option value="">Select Employee</option>
                                            {filteredEmployees.map((e) => (
                                                <option key={e.employeeId ?? e.empId ?? e.EmployeeId} value={e.employeeId ?? e.empId ?? e.EmployeeId}>
                                                    {e.employeeName ?? e.empName ?? e.name} ({e.employeeCode ?? e.empCode ?? ""})
                                                </option>
                                            ))}
                                        </Field>
                                        <ErrorMessage name="EmployeeId" component="div" style={{ color: "red", fontSize: "12px" }} />
                                    </div>
                                </Col> */}

                                <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="EmployeeId" className="form-label">
                                            Employee <span style={{ color: "#EC1117" }}>*</span>
                                        </label>

                                        <Select
                                            inputId="EmployeeId"
                                            name="EmployeeId"
                                            classNamePrefix="react-select"
                                            placeholder="Select Employee"
                                            isSearchable={true}
                                            isDisabled={!values.WardId}
                                            options={filteredEmployees.map((e) => ({
                                                value: e.employeeId ?? e.empId ?? e.EmployeeId,
                                                label: `${e.employeeName ?? e.empName ?? e.name} (${e.employeeCode ?? e.empCode ?? ""})`,
                                            }))}
                                            value={
                                                filteredEmployees
                                                    .map((e) => ({
                                                        value: e.employeeId ?? e.empId ?? e.EmployeeId,
                                                        label: `${e.employeeName ?? e.empName ?? e.name} (${e.employeeCode ?? e.empCode ?? ""})`,
                                                    }))
                                                    .find((option) => option.value === values.EmployeeId) || null
                                            }
                                            onChange={(selectedOption) => {
                                                setFieldValue(
                                                    "EmployeeId",
                                                    selectedOption ? selectedOption.value : ""
                                                );
                                            }}
                                        />

                                        <ErrorMessage
                                            name="EmployeeId"
                                            component="div"
                                            style={{ color: "red", fontSize: "12px" }}
                                        />
                                    </div>
                                </Col>


                                <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="PunchInTime" className="form-label">
                                            Punch In Time <span style={{ color: "#EC1117" }}>*</span>
                                        </label>
                                        <Field
                                            type="datetime-local"
                                            name="PunchInTime"
                                            id="PunchInTime"
                                            className="form-control"
                                            max={getCurrentDate() + "T23:59"}
                                        />
                                        <ErrorMessage name="PunchInTime" component="div" style={{ color: "red", fontSize: "12px" }} />
                                    </div>
                                </Col>

                                <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="PunchOutTime" className="form-label">
                                            Punch Out Time <span style={{ color: "#EC1117" }}>*</span>
                                        </label>
                                        <Field
                                            type="datetime-local"
                                            name="PunchOutTime"
                                            id="PunchOutTime"
                                            className="form-control"
                                            min={values.PunchInTime}
                                        />
                                        <ErrorMessage name="PunchOutTime" component="div" style={{ color: "red", fontSize: "12px" }} />
                                    </div>
                                </Col>

                                {/* <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="ShiftId" className="form-label">
                                            Shift <span style={{ color: "#EC1117" }}>*</span>
                                        </label>
                                        <Field
                                            as="select"
                                            name="ShiftId"
                                            id="ShiftId"
                                            className="form-select"
                                        >
                                            <option value="">Select Shift</option>
                                            {shiftOptions
                                                .filter((s) => s.isActive)
                                                .map((s) => (
                                                    <option key={s.shiftId} value={s.shiftId}>
                                                        {s.shiftName}
                                                        {s.startTime && s.endTime
                                                            ? ` [${s.startTime} - ${s.endTime}]`
                                                            : ""}
                                                    </option>
                                                ))}
                                        </Field>
                                        <ErrorMessage name="ShiftId" component="div" style={{ color: "red", fontSize: "12px" }} />
                                    </div>
                                </Col> */}

                                <Col md="12">
                                    <div className="form-group">
                                        <label htmlFor="ShiftId" className="form-label">
                                            Shift <span style={{ color: "#EC1117" }}>*</span>
                                        </label>

                                        <Select
                                            inputId="ShiftId"
                                            name="ShiftId"
                                            classNamePrefix="react-select"
                                            placeholder="Select Shift"
                                            isSearchable={true}
                                            options={shiftOptions
                                                .filter((s) => s.isActive)
                                                .map((s) => ({
                                                    value: s.shiftId,
                                                    label: `${s.shiftName}${s.startTime && s.endTime
                                                            ? ` [${s.startTime} - ${s.endTime}]`
                                                            : ""
                                                        }`,
                                                }))}
                                            value={
                                                shiftOptions
                                                    .filter((s) => s.isActive)
                                                    .map((s) => ({
                                                        value: s.shiftId,
                                                        label: `${s.shiftName}${s.startTime && s.endTime
                                                                ? ` [${s.startTime} - ${s.endTime}]`
                                                                : ""
                                                            }`,
                                                    }))
                                                    .find((option) => option.value === values.ShiftId) || null
                                            }
                                            onChange={(selectedOption) => {
                                                setFieldValue(
                                                    "ShiftId",
                                                    selectedOption ? selectedOption.value : ""
                                                );
                                            }}
                                        />

                                        <ErrorMessage
                                            name="ShiftId"
                                            component="div"
                                            style={{ color: "red", fontSize: "12px" }}
                                        />
                                    </div>
                                </Col>

                                <Col md="12">
                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            style={{ minWidth: "80px" }}
                                            onClick={closeModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="deployment-button"
                                            style={{ minWidth: "80px" }}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <l-bouncy size="20" speed="1.75" color="white"></l-bouncy>
                                            ) : isEditAttendance ? (
                                                <><i className="fa-solid fa-check done-container"></i> Update</>
                                            ) : (
                                                "Save"
                                            )}
                                        </button>
                                    </div>
                                </Col>

                            </Row>
                        </Form>
                    )}
                </Formik>
            </Modal.Body>
        </Modal>
    );
};